import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Input,
    Label,
    Row,
    Spinner,
    Badge,
    Table,
} from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { APIClient } from "../../helpers/api_helper";
import { GET_QUESTION_PERFORMANCE_REPORT, GET_COURSES } from "../../helpers/custom_helper/custom_url_helper";
import { toast, ToastContainer } from "react-toastify";

const api = new APIClient();

const QuestionPerformanceReport = () => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [courses, setCourses] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [topicAreas, setTopicAreas] = useState([]);

    // Server-side filters (trigger API call)
    const [selectedCourse, setSelectedCourse] = useState("");
    const [selectedLang, setSelectedLang] = useState("");
    const [selectedTopic, setSelectedTopic] = useState("");
    const [selectedQType, setSelectedQType] = useState("");

    // Client-side filters (filter already-fetched data)
    const [searchTerm, setSearchTerm] = useState("");
    const [filterPriority, setFilterPriority] = useState("ALL");
    const [sortConfig, setSortConfig] = useState({ key: "incorrect_percentage", direction: "desc" });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 20;

    // ---------- Load dropdown data once on mount ----------
    useEffect(() => {
        const fetchDropdowns = async () => {
            try {
                // --- Courses ---
                const courseRes = await api.get(GET_COURSES);
                const coursesArr = courseRes?.data?.courses?.data || [];
                setCourses(Array.isArray(coursesArr) ? coursesArr : []);

                // --- Languages (available from same endpoint OR separate) ---
                const langsFromCourse = courseRes?.data?.langs?.data || [];
                if (Array.isArray(langsFromCourse) && langsFromCourse.length > 0) {
                    setLanguages(langsFromCourse);
                } else {
                    // Fallback: dedicated endpoint
                    const langRes = await api.get("/get-all-lang");
                    setLanguages(langRes?.data || []);
                }

                // --- Topic Areas ---
                const topicRes = await api.get("/get-all-topics");
                const topicsArr = topicRes?.data?.topicAreas?.data || [];
                setTopicAreas(Array.isArray(topicsArr) ? topicsArr : []);
            } catch (error) {
                console.error("Error fetching dropdown data:", error);
            }
        };
        fetchDropdowns();
    }, []);

    // ---------- Fetch report from API ----------
    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (selectedCourse) params.course_id = selectedCourse;
            if (selectedLang) params.lang = selectedLang;
            if (selectedTopic) params.topic_id = selectedTopic;
            if (selectedQType) params.q_type = selectedQType;

            const response = await api.get(GET_QUESTION_PERFORMANCE_REPORT, params);
            if (response && (response.status || response.success)) {
                setReportData(response.data);
            } else {
                toast.error(response?.message || "Failed to fetch report");
                setReportData(null);
            }
        } catch (error) {
            toast.error("Error fetching performance report");
            console.error(error);
            setReportData(null);
        }
        setLoading(false);
    }, [selectedCourse, selectedLang, selectedTopic, selectedQType]);

    // Re-fetch whenever any server-side filter changes
    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    // Reset to page 1 when any filter/search/sort changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterPriority, sortConfig, reportData]);

    // ---------- Sort handler ----------
    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return "ri-arrow-up-down-line text-muted";
        return sortConfig.direction === "asc"
            ? "ri-arrow-up-s-fill text-primary"
            : "ri-arrow-down-s-fill text-primary";
    };

    // ---------- Client-side filter + sort + pagination ----------
    const filteredQuestions = useMemo(() => {
        if (!reportData?.questions) return [];
        let filtered = Array.isArray(reportData.questions)
            ? [...reportData.questions]
            : [];

        // Text search
        if (searchTerm.trim()) {
            const terms = searchTerm.toLowerCase().trim().split(/\s+/);
            filtered = filtered.filter((q) => {
                const haystack = [
                    q.title, q.code, q.topic_area, q.course_names,
                    q.lang_name, q.lang_code,
                    q.q_type == 1 ? "common" : "specific",
                ].filter(Boolean).join(" ").toLowerCase();
                return terms.every((t) => haystack.includes(t));
            });
        }

        // Priority filter
        if (filterPriority !== "ALL") {
            filtered = filtered.filter((q) => q.priority === filterPriority);
        }

        // Sort
        filtered.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];
            // Treat numeric strings as numbers for proper sorting
            if (!isNaN(aVal) && !isNaN(bVal)) {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            }
            if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });

        return filtered;
    }, [reportData, searchTerm, filterPriority, sortConfig]);

    // Paginated slice
    const totalPages = Math.ceil(filteredQuestions.length / pageSize) || 1;
    const paginatedQuestions = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredQuestions.slice(start, start + pageSize);
    }, [filteredQuestions, currentPage]);

    // ---------- Helper renderers ----------
    const getCourseName = (course) => {
        if (course.course_translation && Array.isArray(course.course_translation) && course.course_translation.length > 0) {
            return course.course_translation[0].full_name || course.short_name;
        }
        return course.short_name || `Course ${course.id}`;
    };

    const getTopicName = (topic) => {
        if (topic.topic_area_translation && Array.isArray(topic.topic_area_translation) && topic.topic_area_translation.length > 0) {
            return topic.topic_area_translation[0].full_name || `Topic ${topic.id}`;
        }
        return `Topic ${topic.id}`;
    };

    const getPriorityBadge = (priority) => {
        switch (priority) {
            case "HIGH":
                return <Badge color="danger" className="px-2 py-1">🔴 HIGH</Badge>;
            case "MEDIUM":
                return <Badge color="warning" className="px-2 py-1">🟡 MEDIUM</Badge>;
            default:
                return <Badge color="success" className="px-2 py-1">🟢 NORMAL</Badge>;
        }
    };

    const getPercentageBadge = (pct) => {
        const val = parseFloat(pct) || 0;
        let color = "success";
        if (val >= 70) color = "danger";
        else if (val >= 50) color = "warning";
        return <span className={`fw-semibold text-${color}`}>{val.toFixed(1)}%</span>;
    };

    document.title = "Question Performance Report | Theory Practice";

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Question Performance Report" pageTitle="Questions" />

                    {/* ── Filters ── */}
                    <Card className="border-0 shadow-sm mb-3">
                        <CardBody>
                            <Row className="g-3">
                                <Col md={2}>
                                    <Label className="form-label fw-semibold mb-1">Course</Label>
                                    <Input
                                        type="select"
                                        className="form-select"
                                        value={selectedCourse}
                                        onChange={(e) => setSelectedCourse(e.target.value)}
                                    >
                                        <option value="">All Courses</option>
                                        {courses.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {getCourseName(c)}
                                            </option>
                                        ))}
                                    </Input>
                                </Col>
                                <Col md={2}>
                                    <Label className="form-label fw-semibold mb-1">Topic Area</Label>
                                    <Input
                                        type="select"
                                        className="form-select"
                                        value={selectedTopic}
                                        onChange={(e) => setSelectedTopic(e.target.value)}
                                    >
                                        <option value="">All Topics</option>
                                        {topicAreas.map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {getTopicName(t)}
                                            </option>
                                        ))}
                                    </Input>
                                </Col>
                                <Col md={2}>
                                    <Label className="form-label fw-semibold mb-1">Language</Label>
                                    <Input
                                        type="select"
                                        className="form-select"
                                        value={selectedLang}
                                        onChange={(e) => setSelectedLang(e.target.value)}
                                    >
                                        <option value="">All Languages</option>
                                        {languages.map((l) => (
                                            <option key={l.id} value={l.lang_short}>
                                                {l.lang}
                                            </option>
                                        ))}
                                    </Input>
                                </Col>
                                <Col md={2}>
                                    <Label className="form-label fw-semibold mb-1">Question Type</Label>
                                    <Input
                                        type="select"
                                        className="form-select"
                                        value={selectedQType}
                                        onChange={(e) => setSelectedQType(e.target.value)}
                                    >
                                        <option value="">All Types</option>
                                        <option value="1">Common</option>
                                        <option value="2">Specific</option>
                                    </Input>
                                </Col>
                                <Col md={2}>
                                    <Label className="form-label fw-semibold mb-1">Priority</Label>
                                    <Input
                                        type="select"
                                        className="form-select"
                                        value={filterPriority}
                                        onChange={(e) => setFilterPriority(e.target.value)}
                                    >
                                        <option value="ALL">All Priorities</option>
                                        <option value="HIGH">🔴 High (≥70%)</option>
                                        <option value="MEDIUM">🟡 Medium (50-69%)</option>
                                        <option value="NORMAL">🟢 Normal (&lt;50%)</option>
                                    </Input>
                                </Col>
                                <Col md={2} className="d-flex align-items-end">
                                    <button
                                        className="btn btn-soft-primary w-100"
                                        onClick={fetchReport}
                                        disabled={loading}
                                    >
                                        <i className="ri-refresh-line me-1"></i> Refresh
                                    </button>
                                </Col>
                            </Row>

                            {/* Search bar */}
                            <Row className="mt-3">
                                <Col md={12}>
                                    <Input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by title, code, topic, course, language, type..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    {/* ── Summary Cards ── */}
                    {reportData?.summary && (
                        <Row className="mb-3">
                            <Col md={4}>
                                <Card className="card-animate border-0 shadow-sm">
                                    <CardBody className="d-flex align-items-center">
                                        <div className="avatar-sm me-3">
                                            <div className="avatar-title bg-soft-primary text-primary rounded-circle fs-3">
                                                <i className="ri-questionnaire-line"></i>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-muted mb-0 text-uppercase fw-medium fs-12">Total Questions</p>
                                            <h4 className="mb-0 fw-bold">{reportData.summary.total_questions}</h4>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="card-animate border-0 shadow-sm">
                                    <CardBody className="d-flex align-items-center">
                                        <div className="avatar-sm me-3">
                                            <div className="avatar-title bg-soft-danger text-danger rounded-circle fs-3">
                                                <i className="ri-error-warning-line"></i>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-muted mb-0 text-uppercase fw-medium fs-12">High Priority (≥70%)</p>
                                            <h4 className="mb-0 fw-bold text-danger">{reportData.summary.high_priority_count}</h4>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="card-animate border-0 shadow-sm">
                                    <CardBody className="d-flex align-items-center">
                                        <div className="avatar-sm me-3">
                                            <div className="avatar-title bg-soft-warning text-warning rounded-circle fs-3">
                                                <i className="ri-percent-line"></i>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-muted mb-0 text-uppercase fw-medium fs-12">Avg. Incorrect Rate</p>
                                            <h4 className="mb-0 fw-bold">{reportData.summary.avg_incorrect_rate}%</h4>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* ── Data Table ── */}
                    <Row>
                        <Col>
                            <Card className="border-0 shadow-sm">
                                <CardHeader className="bg-white border-bottom">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <h5 className="card-title mb-0 fw-semibold">
                                            <i className="ri-bar-chart-2-line me-2 text-primary"></i>
                                            Question Performance Details
                                        </h5>
                                        <span className="text-muted fs-12">
                                            Showing {paginatedQuestions.length} of {filteredQuestions.length} questions
                                            {filteredQuestions.length !== (reportData?.questions?.length || 0) &&
                                                ` (filtered from ${reportData?.questions?.length || 0})`}
                                        </span>
                                    </div>
                                </CardHeader>
                                <CardBody className="p-0">
                                    {loading ? (
                                        <div className="d-flex justify-content-center py-5">
                                            <Spinner color="primary"> Loading... </Spinner>
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <Table className="table-hover table-striped align-middle mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th style={{ width: "4%" }}>#</th>
                                                        <th style={{ cursor: "pointer", width: "7%" }} onClick={() => handleSort("code")}>
                                                            Code <i className={getSortIcon("code")}></i>
                                                        </th>
                                                        <th style={{ width: "22%" }}>Question</th>
                                                        <th style={{ cursor: "pointer", width: "10%" }} onClick={() => handleSort("topic_area")}>
                                                            Topic Area <i className={getSortIcon("topic_area")}></i>
                                                        </th>
                                                        <th style={{ cursor: "pointer", width: "8%" }} onClick={() => handleSort("course_names")}>
                                                            Course(s) <i className={getSortIcon("course_names")}></i>
                                                        </th>
                                                        <th style={{ cursor: "pointer", width: "8%" }} onClick={() => handleSort("lang_name")}>
                                                            Language <i className={getSortIcon("lang_name")}></i>
                                                        </th>
                                                        <th style={{ cursor: "pointer", width: "7%" }} onClick={() => handleSort("q_type")}>
                                                            Type <i className={getSortIcon("q_type")}></i>
                                                        </th>
                                                        <th className="text-center" style={{ cursor: "pointer", width: "7%" }} onClick={() => handleSort("total_appearances")}>
                                                            Appeared <i className={getSortIcon("total_appearances")}></i>
                                                        </th>
                                                        <th className="text-center" style={{ cursor: "pointer", width: "6%" }} onClick={() => handleSort("correct_count")}>
                                                            Correct <i className={getSortIcon("correct_count")}></i>
                                                        </th>
                                                        <th className="text-center" style={{ cursor: "pointer", width: "7%" }} onClick={() => handleSort("incorrect_count")}>
                                                            Incorrect <i className={getSortIcon("incorrect_count")}></i>
                                                        </th>
                                                        <th className="text-center" style={{ cursor: "pointer", width: "8%" }} onClick={() => handleSort("incorrect_percentage")}>
                                                            Incorrect % <i className={getSortIcon("incorrect_percentage")}></i>
                                                        </th>
                                                        <th className="text-center" style={{ width: "8%" }}>Priority</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {paginatedQuestions.length > 0 ? (
                                                        paginatedQuestions.map((q, index) => (
                                                            <tr key={`${q.id}-${q.lang_code}-${index}`}>
                                                                <td className="text-muted">
                                                                    {(currentPage - 1) * pageSize + index + 1}
                                                                </td>
                                                                <td>
                                                                    <span className="fw-medium text-primary">{q.code}</span>
                                                                </td>
                                                                <td>
                                                                    <span
                                                                        className="text-truncate d-inline-block"
                                                                        style={{ maxWidth: "300px" }}
                                                                        title={q.title}
                                                                    >
                                                                        {q.title || "—"}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill" style={{ backgroundColor: "#e8f5f0", color: "#0d9668", fontSize: "12px", fontWeight: 500 }}>
                                                                        <i className="ri-bookmark-line" style={{ fontSize: "11px" }}></i>
                                                                        {q.topic_area || "—"}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill" style={{ backgroundColor: "#f0e8ff", color: "#6f42c1", fontSize: "12px", fontWeight: 500 }}>
                                                                        <i className="ri-graduation-cap-line" style={{ fontSize: "11px" }}></i>
                                                                        {q.course_names || "—"}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill" style={{ backgroundColor: "#e8f0fe", color: "#3b71ca", fontSize: "12px", fontWeight: 500 }}>
                                                                        <i className="ri-global-line" style={{ fontSize: "11px" }}></i>
                                                                        {q.lang_name || q.lang_code || "—"}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill" style={{
                                                                        backgroundColor: q.q_type == 1 ? "#eef2ff" : "#fff4e6",
                                                                        color: q.q_type == 1 ? "#4f46e5" : "#b45309",
                                                                        fontSize: "12px", fontWeight: 500
                                                                    }}>
                                                                        <i className={q.q_type == 1 ? "ri-share-line" : "ri-focus-2-line"} style={{ fontSize: "11px" }}></i>
                                                                        {q.q_type == 1 ? "Common" : "Specific"}
                                                                    </span>
                                                                </td>
                                                                <td className="text-center fw-medium">{q.total_appearances}</td>
                                                                <td className="text-center">
                                                                    <span className="text-success fw-medium">{q.correct_count}</span>
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className="text-danger fw-medium">{q.incorrect_count}</span>
                                                                </td>
                                                                <td className="text-center">
                                                                    {getPercentageBadge(q.incorrect_percentage)}
                                                                </td>
                                                                <td className="text-center">{getPriorityBadge(q.priority)}</td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="12" className="text-center py-4">
                                                                <div className="text-muted">
                                                                    <i className="ri-inbox-line fs-1 d-block mb-2"></i>
                                                                    No data found
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    )}

                                    {/* Pagination */}
                                    {!loading && filteredQuestions.length > pageSize && (
                                        <div className="d-flex align-items-center justify-content-between border-top px-3 py-3">
                                            <span className="text-muted fs-13">
                                                Page {currentPage} of {totalPages}
                                            </span>
                                            <ul className="pagination pagination-sm mb-0">
                                                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                                    <button className="page-link" onClick={() => setCurrentPage(1)}>
                                                        <i className="ri-arrow-left-double-line"></i>
                                                    </button>
                                                </li>
                                                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                                    <button className="page-link" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
                                                        <i className="ri-arrow-left-s-line"></i>
                                                    </button>
                                                </li>
                                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                    let page;
                                                    if (totalPages <= 5) {
                                                        page = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        page = i + 1;
                                                    } else if (currentPage >= totalPages - 2) {
                                                        page = totalPages - 4 + i;
                                                    } else {
                                                        page = currentPage - 2 + i;
                                                    }
                                                    return (
                                                        <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                                                            <button className="page-link" onClick={() => setCurrentPage(page)}>
                                                                {page}
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                                    <button className="page-link" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>
                                                        <i className="ri-arrow-right-s-line"></i>
                                                    </button>
                                                </li>
                                                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                                    <button className="page-link" onClick={() => setCurrentPage(totalPages)}>
                                                        <i className="ri-arrow-right-double-line"></i>
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    <ToastContainer closeButton={false} limit={1} />
                </Container>
            </div>
        </React.Fragment>
    );
};

export default QuestionPerformanceReport;
